import ContentTranslationsEditor from '../../components/admin/ContentTranslationsEditor';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePortfolio, PortfolioItem } from '../../context/PortfolioContext';
import { uploadImage } from '../../services/uploadService';
import { getImageUrl } from '../../utils/imageUtils';
import {
  ArrowLeft,
  Calendar,
  Edit,
  Folder,
  Image as ImageIcon,
  Link as LinkIcon,
  Loader2,
  Plus,
  Save,
  Trash,
  UploadCloud,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const emptyProject = (): Partial<PortfolioItem> => ({
  sourceLanguage: 'en',
  translations: {},
  title: '',
  slug: '',
  description: '',
  longDescription: '',
  image: '',
  technologies: [],
  category: '',
  date: new Date().toISOString().split('T')[0],
  featured: false,
  gallery: [],
});

const slugFromTitle = (title: string) =>
  title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseError = error as { response?: { data?: { message?: string } }; message?: string };
    return responseError.response?.data?.message || responseError.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const AdminPortfolio = () => {
  const { projects, addProject, updateProject, deleteProject } = usePortfolio();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('edit');
  const isNewProject = searchParams.get('new') === 'true';
  const isEditing = Boolean(editId || isNewProject);

  const [currentProject, setCurrentProject] = useState<Partial<PortfolioItem>>(emptyProject);
  const [newTechnology, setNewTechnology] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isGalleryUploading, setIsGalleryUploading] = useState(false);
  const [galleryUploadError, setGalleryUploadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (editId) {
      const projectToEdit = projects.find((project) => project.id === editId);
      if (projectToEdit) {
        setCurrentProject(projectToEdit);
      }
      return;
    }

    if (isNewProject) {
      setCurrentProject(emptyProject());
      return;
    }

    setCurrentProject(emptyProject());
  }, [editId, isNewProject, projects]);

  const categories = useMemo(
    () => [
      'Web Application',
      'Mobile Application',
      'Data Visualization',
      'IoT',
      'Developer Tools',
      ...new Set(projects.map((project) => project.category).filter(Boolean)),
    ],
    [projects]
  );

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [projects]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;

    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      setCurrentProject((previous) => ({
        ...previous,
        [name]: checked,
      }));
      return;
    }

    setCurrentProject((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSlugGeneration = () => {
    if (!currentProject.title) return;
    setCurrentProject((previous) => ({
      ...previous,
      slug: slugFromTitle(currentProject.title || ''),
    }));
  };

  const addTechnology = () => {
    const technology = newTechnology.trim();
    if (!technology || currentProject.technologies?.includes(technology)) return;

    setCurrentProject((previous) => ({
      ...previous,
      technologies: [...(previous.technologies || []), technology],
    }));
    setNewTechnology('');
  };

  const removeTechnology = (technology: string) => {
    setCurrentProject((previous) => ({
      ...previous,
      technologies: previous.technologies?.filter((item) => item !== technology) || [],
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setUploadError(null);
    setIsUploading(true);

    try {
      const result = await uploadImage(file);
      setCurrentProject((previous) => ({
        ...previous,
        image: result.filePath,
      }));
      toast.success('Main image uploaded successfully');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Upload failed');
      setUploadError(message);
      toast.error(`Upload failed: ${message}`);
    } finally {
      setSelectedFile(null);
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleGalleryImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setGalleryUploadError(null);
    setIsGalleryUploading(true);

    try {
      const result = await uploadImage(file);
      setCurrentProject((previous) => ({
        ...previous,
        gallery: [...(previous.gallery || []), result.filePath],
      }));
      toast.success('Gallery image added successfully');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Upload failed');
      setGalleryUploadError(message);
      toast.error(`Gallery upload failed: ${message}`);
    } finally {
      setIsGalleryUploading(false);
      event.target.value = '';
    }
  };

  const removeGalleryImage = (image: string) => {
    setCurrentProject((previous) => ({
      ...previous,
      gallery: previous.gallery?.filter((item) => item !== image) || [],
    }));
  };

  const handleSave = async () => {
    if (!currentProject.title || !currentProject.slug || !currentProject.description || !currentProject.category) {
      toast.error('Please fill in title, slug, description, and category.');
      return;
    }

    if (!currentProject.image) {
      toast.error('Please upload a main image for the project.');
      return;
    }

    setIsSaving(true);
    try {
      if (editId) {
        await updateProject(editId, currentProject as PortfolioItem);
        toast.success('Project updated successfully');
      } else {
        await addProject(currentProject as Omit<PortfolioItem, 'id'>);
        toast.success('Project created successfully');
      }
      navigate('/admin');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Save failed');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editId || !window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await deleteProject(editId);
      toast.success('Project deleted successfully');
      navigate('/admin');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Delete failed');
      toast.error(message);
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-container">
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-start gap-4">
            <Link to="/admin" className="admin-icon-button mt-1" aria-label="Back to admin">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <p className="admin-section-title mb-3">Portfolio</p>
              <h1 className="text-3xl font-bold sm:text-4xl">
                {editId ? 'Edit Portfolio Project' : isNewProject ? 'Create New Project' : 'Portfolio Projects'}
              </h1>
            </div>
          </div>

          {isEditing && (
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={handleSave} disabled={isSaving} className="btn btn-primary">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Project
              </button>
              {editId && (
                <button type="button" onClick={handleDelete} className="btn btn-secondary text-error">
                  <Trash className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <section className="admin-panel p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="label">Title*</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className="input"
                    value={currentProject.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                  <div>
                    <label htmlFor="slug" className="label">Slug*</label>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto]">
                      <input
                        type="text"
                        id="slug"
                        name="slug"
                        className="input rounded-r-none"
                        value={currentProject.slug}
                        onChange={handleInputChange}
                        required
                      />
                      <button
                        type="button"
                        onClick={handleSlugGeneration}
                        className="bg-primary/20 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/30"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="date" className="label">Date*</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      className="input"
                      value={currentProject.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="label">Short Description*</label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    className="input"
                    value={currentProject.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="longDescription" className="label">Full Description*</label>
                  <textarea
                    id="longDescription"
                    name="longDescription"
                    rows={20}
                    className="input min-h-[420px] font-mono text-sm leading-6"
                    value={currentProject.longDescription}
                    onChange={handleInputChange}
                    required
                  />
                  <p className="mt-2 text-xs text-foreground/50">Description supports Markdown formatting.</p>
                </div>
                  <ContentTranslationsEditor
                    kind="project"
                    sourceLanguage={currentProject.sourceLanguage || 'en'}
                    translations={currentProject.translations || {}}
                    onSourceLanguageChange={sourceLanguage => setCurrentProject(previous => ({ ...previous, sourceLanguage }))}
                    onChange={translations => setCurrentProject(previous => ({ ...previous, translations }))}
                  />
              </div>

              <aside className="space-y-6">
                <div>
                  <label className="label">Main Image*</label>
                  <div className="admin-panel-muted p-4">
                    {currentProject.image ? (
                      <img
                        src={getImageUrl(currentProject.image)}
                        alt="Current main"
                        className="mb-4 h-44 w-full rounded-lg object-cover"
                      />
                    ) : (
                      <div className="mb-4 flex h-44 flex-col items-center justify-center rounded-lg border border-dashed border-foreground/15 text-center">
                        <ImageIcon className="h-10 w-10 text-foreground/35" />
                        <p className="mt-2 text-sm text-foreground/55">No image uploaded yet.</p>
                      </div>
                    )}

                    <label htmlFor="project-image-upload" className={`btn btn-secondary w-full cursor-pointer ${isUploading ? 'opacity-50' : ''}`}>
                      <UploadCloud className="h-4 w-4" />
                      {isUploading ? 'Uploading...' : selectedFile ? selectedFile.name : 'Upload main image'}
                      <input
                        id="project-image-upload"
                        name="project-image-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleImageUpload}
                        accept="image/*"
                        disabled={isUploading}
                      />
                    </label>
                    {uploadError && <p className="mt-2 text-sm text-error">{uploadError}</p>}
                    <p className="mt-3 text-xs text-foreground/50">Max 5MB. JPG, PNG, GIF, WEBP.</p>
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="label">Category*</label>
                  <select
                    id="category"
                    name="category"
                    className="input"
                    value={currentProject.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="url" className="label">Project URL</label>
                  <input
                    type="text"
                    id="url"
                    name="url"
                    className="input"
                    value={currentProject.url || ''}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label htmlFor="github" className="label">GitHub URL</label>
                  <input
                    type="text"
                    id="github"
                    name="github"
                    className="input"
                    value={currentProject.github || ''}
                    onChange={handleInputChange}
                    placeholder="https://github.com/username/repo"
                  />
                </div>

                <label className="flex items-center gap-3 text-sm text-foreground/80">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={Boolean(currentProject.featured)}
                    onChange={handleInputChange}
                    className="h-4 w-4 rounded border-foreground/20 bg-card text-primary focus:ring-primary"
                  />
                  Featured Project
                </label>

                <div>
                  <label className="label">Technologies</label>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {currentProject.technologies?.map((technology) => (
                      <span key={technology} className="admin-chip">
                        {technology}
                        <button type="button" onClick={() => removeTechnology(technology)} className="text-primary/70 hover:text-primary">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_44px]">
                    <input
                      type="text"
                      className="input rounded-r-none"
                      placeholder="Add technology"
                      value={newTechnology}
                      onChange={(event) => setNewTechnology(event.target.value)}
                      onKeyDown={(event) => event.key === 'Enter' && addTechnology()}
                    />
                    <button type="button" onClick={addTechnology} className="bg-primary/20 text-primary hover:bg-primary/30">
                      <Plus className="mx-auto h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label">Gallery Images</label>
                  <div className="admin-panel-muted p-4">
                    <label htmlFor="gallery-image-upload" className={`btn btn-secondary w-full cursor-pointer ${isGalleryUploading ? 'opacity-50' : ''}`}>
                      <UploadCloud className="h-4 w-4" />
                      {isGalleryUploading ? 'Uploading...' : 'Upload gallery image'}
                      <input
                        id="gallery-image-upload"
                        name="gallery-image-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleGalleryImageUpload}
                        accept="image/*"
                        disabled={isGalleryUploading}
                      />
                    </label>
                    {galleryUploadError && <p className="mt-2 text-sm text-error">{galleryUploadError}</p>}

                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2">
                      {currentProject.gallery?.map((image) => (
                        <div key={image} className="group relative overflow-hidden rounded-lg border border-foreground/10">
                          <img src={getImageUrl(image)} alt="Gallery" className="h-24 w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(image)}
                            className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground opacity-0 transition-opacity group-hover:opacity-100"
                            title="Remove image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </section>
        ) : (
          <section className="admin-panel p-5 sm:p-6">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold">All Portfolio Projects</h2>
              <Link to="/admin/portfolio?new=true" className="btn btn-primary">
                <Plus className="h-4 w-4" />
                Create New Project
              </Link>
            </div>

            {sortedProjects.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {sortedProjects.map((project) => (
                  <article key={project.id} className="admin-panel-muted p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="mb-3 flex items-center gap-2 text-primary">
                          <Folder className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-[0.18em]">Project</span>
                        </div>
                        <h3 className="line-clamp-2 font-semibold">{project.title}</h3>
                        <p className="mt-2 flex items-center gap-2 text-sm text-foreground/55">
                          <Calendar className="h-4 w-4" />
                          {project.date}
                        </p>
                      </div>
                      <Link to={`/admin/portfolio?edit=${project.id}`} className="admin-icon-button" title="Edit project">
                        <Edit className="h-4 w-4" />
                      </Link>
                    </div>

                    <p className="mt-4 line-clamp-2 text-sm text-foreground/60">{project.description}</p>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <span className="admin-chip">{project.category}</span>
                      {project.url && (
                        <a
                          href={project.url}
                          className="inline-flex items-center gap-1 text-sm text-foreground/55 hover:text-primary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkIcon className="h-4 w-4" />
                          View project
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="admin-panel-muted p-8 text-center">
                <p className="mb-4 text-foreground/70">No portfolio projects found</p>
                <Link to="/admin/portfolio?new=true" className="btn btn-primary">
                  <Plus className="h-4 w-4" />
                  Create Your First Project
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminPortfolio;
